
import { useEffect, useMemo } from "react";

// Import dashboard components
import { DashboardControls } from "./dashboard/DashboardControls";
import { WidgetsModal } from "@/components/dashboard/WidgetsModal";
import { DashboardWidgets } from "./dashboard/DashboardWidgets";

// Import hooks
import { useDashboardData } from "./dashboard/useDashboardData";
import { useDashboardLayout } from "@/hooks/useDashboardLayout";

export function AdminDashboard() {
  const {
    stats,
    newUsersTimeline,
    errorStats,
    emailStats,
    recentErrors,
    activeUsers,
    isLoading,
    fetchDashboardData
  } = useDashboardData();
  
  const {
    handlesVisible,
    isSaving,
    savedToDb,
    layoutInitialized,
    isWidgetsModalOpen,
    allWidgetItems,
    setIsWidgetsModalOpen,
    toggleHandlesVisibility,
    resetDashboardLayout,
    handleSaveLayout,
    handleRemoveWidget,
    handleAddWidget,
    handleWidgetsReorder,
    updateWidgetSize
  } = useDashboardLayout('admin-dashboard');

  // Check if we have any meaningful admin data to display
  const hasData = useMemo(() => {
    return (
      stats.totalUsers > 0 ||
      newUsersTimeline.some(item => item.count > 0) ||
      errorStats.length > 0 ||
      emailStats.length > 0 ||
      recentErrors.length > 0 ||
      activeUsers.length > 0
    );
  }, [stats, newUsersTimeline, errorStats, emailStats, recentErrors, activeUsers]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  return (
    <div className="space-y-4">
      <DashboardControls 
        handlesVisible={handlesVisible}
        toggleHandlesVisibility={toggleHandlesVisibility}
        saveLayoutToDatabase={handleSaveLayout}
        resetDashboardLayout={resetDashboardLayout}
        isSaving={isSaving}
        savedToDb={savedToDb}
        openWidgetsModal={() => setIsWidgetsModalOpen(true)}
      />
      
      <WidgetsModal
        isOpen={isWidgetsModalOpen}
        onClose={() => setIsWidgetsModalOpen(false)}
        activeWidgetIds={allWidgetItems.map(item => item.id)}
        onAddWidget={handleAddWidget}
        dashboardType="admin"
      />
      
      {isLoading && !layoutInitialized ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary mx-auto mb-4"></div>
            <h3 className="text-lg font-medium">Loading dashboard...</h3>
          </div>
        </div>
      ) : layoutInitialized && (
        <DashboardWidgets
          widgetItems={allWidgetItems}
          handlesVisible={handlesVisible}
          onRemove={handleRemoveWidget}
          onResize={updateWidgetSize}
          onReorder={handleWidgetsReorder}
          stats={stats}
          newUsersTimeline={newUsersTimeline}
          errorStats={errorStats}
          emailStats={emailStats}
          recentErrors={recentErrors}
          activeUsers={activeUsers}
          hasData={hasData}
        />
      )}
    </div>
  );
}
